import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState("");
  const [countdown, setCountdown] = useState(5); // Compte à rebours de 5 secondes

  const { token } = useParams();
  const navigate = useNavigate();

  // Vérifier la validité du token au chargement
  useEffect(() => {
    if (!token) {
      toast.error("Token de réinitialisation manquant");
      navigate("/login");
      return;
    }

    // Log pour déboguer
    console.log("Page ResetPassword chargée avec token:", token);
  }, [token, navigate]);

  // Évaluer la force du mot de passe
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordFeedback("");
      return;
    }

    let strength = 0;
    let feedback = "";

    // Longueur minimum
    if (password.length >= 8) {
      strength += 1;
    } else {
      feedback = "Le mot de passe doit contenir au moins 8 caractères";
    }

    // Présence de chiffres
    if (/\d/.test(password)) {
      strength += 1;
    }

    // Présence de lettres minuscules
    if (/[a-z]/.test(password)) {
      strength += 1;
    }

    // Présence de lettres majuscules
    if (/[A-Z]/.test(password)) {
      strength += 1;
    }

    // Présence de caractères spéciaux
    if (/[^A-Za-z0-9]/.test(password)) {
      strength += 1;
    }

    // Définir le feedback en fonction de la force
    if (strength === 0 || strength === 1) {
      feedback = feedback || "Mot de passe faible";
    } else if (strength === 2) {
      feedback = "Mot de passe moyen";
    } else if (strength === 3) {
      feedback = "Mot de passe correct";
    } else if (strength === 4) {
      feedback = "Mot de passe fort";
    } else {
      feedback = "Mot de passe très fort";
    }

    setPasswordStrength(strength);
    setPasswordFeedback(feedback);
  }, [password]);

  // Effet pour le compte à rebours et la redirection
  useEffect(() => {
    let timer;

    if (resetSuccess && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (resetSuccess && countdown === 0) {
      navigate("/login");
    }

    return () => {
      clearTimeout(timer);
    };
  }, [resetSuccess, countdown, navigate]);

  const getPasswordStrengthClass = () => {
    if (passwordStrength === 0) return "";
    if (passwordStrength <= 2) return "bg-danger";
    if (passwordStrength === 3) return "bg-warning";
    return "bg-success";
  };

  // Composant pour le compte à rebours circulaire
  const CountdownCircle = ({ seconds }) => {
    const percentage = (seconds / 5) * 100; // 5 étant le temps total
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="position-relative d-inline-block my-3">
        <svg width="80" height="80" viewBox="0 0 80 80">
          {/* Cercle de fond */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke="#e9ecef"
            strokeWidth="8"
          />
          {/* Cercle de progression */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke="#198754" // Couleur verte pour le succès
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 40 40)"
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
        </svg>
        <span className="position-absolute top-50 start-50 translate-middle fw-bold fs-4">
          {seconds}
        </span>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier que les mots de passe correspondent
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    // Vérifier la longueur minimale
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setIsLoading(true);

    try {
      // Appel API pour réinitialiser le mot de passe
      await axios.post(`/api/auth/reset-password/${token}`, { password });

      setResetSuccess(true);
      toast.success("Votre mot de passe a été modifié avec succès");

      // Le compte à rebours et la redirection sont maintenant gérés par l'effet
    } catch (error) {
      console.error("Erreur de réinitialisation:", error);

      if (
        error.response?.status === 400 &&
        error.response?.data?.message === "Le token est invalide ou a expiré"
      ) {
        setIsExpired(true);
        toast.error("Le lien de réinitialisation a expiré ou est invalide");
      } else {
        const errorMessage =
          error.response?.data?.message ||
          "Une erreur est survenue lors de la réinitialisation du mot de passe";
        toast.error(errorMessage);
      }

      setIsLoading(false);
    }
  };

  // Si le token est expiré, afficher un message approprié
  if (isExpired) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div
          className="card shadow"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <div className="card-body p-4 p-lg-5 text-center">
            <i
              className="bi bi-exclamation-triangle-fill text-warning"
              style={{ fontSize: "3rem" }}
            ></i>
            <h2 className="card-title text-center my-3">Lien expiré</h2>
            <p>Le lien de réinitialisation a expiré ou n'est plus valide.</p>
            <p>
              Les liens de réinitialisation sont valables pendant 15 minutes
              pour des raisons de sécurité.
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate("/login")}
            >
              Retourner à la page de connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100">
      <div className="card shadow" style={{ maxWidth: "500px", width: "100%" }}>
        <div className="card-body p-4 p-lg-5">
          <h2 className="card-title text-center mb-4">
            <i className="bi bi-shield-lock-fill me-2"></i>
            Définir un nouveau mot de passe
          </h2>

          {resetSuccess ? (
            <div className="text-center">
              <div className="alert alert-success">
                <i className="bi bi-check-circle-fill me-2"></i>
                Mot de passe modifié avec succès!
              </div>
              <p className="mb-0">
                Vous allez être redirigé vers la page de connexion dans :
              </p>

              {/* Compte à rebours circulaire au lieu du spinner */}
              <CountdownCircle seconds={countdown} />

              <button
                className="btn btn-success"
                onClick={(e) => e.preventDefault()}
                disabled
              >
                Redirection en cours...
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="text-muted mb-4">
                Veuillez choisir un nouveau mot de passe sécurisé pour votre
                compte.
              </p>

              {/* Nouveau mot de passe */}
              <div className="mb-4">
                <label htmlFor="password" className="form-label fw-semibold">
                  Nouveau mot de passe
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-key-fill"></i>
                  </span>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    placeholder="Entrez votre nouveau mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength="8"
                  />
                </div>
                {password && (
                  <>
                    <div className="progress mt-2" style={{ height: "6px" }}>
                      <div
                        className={`progress-bar ${getPasswordStrengthClass()}`}
                        role="progressbar"
                        style={{ width: `${passwordStrength * 20}%` }}
                        aria-valuenow={passwordStrength * 20}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <small
                      className={`d-block mt-1 ${
                        passwordStrength <= 2
                          ? "text-danger"
                          : passwordStrength === 3
                          ? "text-warning"
                          : "text-success"
                      }`}
                    >
                      {passwordFeedback}
                    </small>
                  </>
                )}
              </div>

              {/* Confirmation du mot de passe */}
              <div className="mb-4">
                <label
                  htmlFor="confirmPassword"
                  className="form-label fw-semibold"
                >
                  Confirmer le mot de passe
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-check-lg"></i>
                  </span>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    placeholder="Confirmez votre nouveau mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <small className="text-danger mt-1">
                    Les mots de passe ne correspondent pas
                  </small>
                )}
              </div>

              {/* Bouton de soumission */}
              <button
                type="submit"
                className="btn btn-primary w-100 fw-bold d-flex align-items-center justify-content-center"
                disabled={
                  isLoading ||
                  password !== confirmPassword ||
                  password.length < 8
                }
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Traitement en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-circle me-2"></i>
                    Réinitialiser mon mot de passe
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
